import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Field, Segment } from 'nallian-shared-ui/lib/components';
import { withTranslation } from 'react-i18next';
import {
  FieldComponent,
  InputFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import { compose } from 'redux';
import VehicleFormFields, { validateVehicleFormFields } from './VehicleFormFields';
import vehicleByLicensePlateService from '../services/vehicleByLicensePlateService';
import addVehicleToPrivateListService from '../services/addVehicleToPrivateListService';
import ExistingVehicleMessage from './ExistingVehicleMessage';
import { history } from 'nallian-shared-ui/lib/config/history';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';

const STEPS = {
  CHOOSE_LICENSEPLATE: 0,
  ADD_TO_PRIVATE_LIST: 1,
  FILL_DETAILS: 2,
};

const STEP_SAVE_BUTTON_MAP = {
  [STEPS.CHOOSE_LICENSEPLATE]: { content: 'next', icon: 'arrow right', iconPosition: 'right' },
  [STEPS.ADD_TO_PRIVATE_LIST]: { content: 'add', icon: 'add', iconPosition: 'left' },
  [STEPS.FILL_DETAILS]: { content: 'save', icon: 'save', iconPosition: 'left' },
};

const FORM_NAME = 'VehicleFormModal';

class VehicleFormModal extends PureComponent {
  componentDidUpdate = prevState => {
    const { vehicleByLicensePlateFetch } = this.props;
    if (!prevState.vehicleByLicensePlateFetch.fulfilled && vehicleByLicensePlateFetch.fulfilled) {
      this.setState({ step: STEPS.ADD_TO_PRIVATE_LIST });
    }
    if (!prevState.vehicleByLicensePlateFetch.rejected && vehicleByLicensePlateFetch.rejected) {
      this.setState({ step: STEPS.FILL_DETAILS });
    }
  };
  getHeader = () => {
    const { licensePlate, step } = this.state;
    const { t } = this.props;
    if (licensePlate) {
      if (step === STEPS.ADD_TO_PRIVATE_LIST) {
        return t('addVehicleToPrivateListWithLicensePlate', { licensePlate });
      }
      if (step === STEPS.FILL_DETAILS) {
        return t('newVehicleWithLicensePlate', { licensePlate });
      }
    }
    return this.props.header;
  };
  handleSubmit = ({ owner, ...values }) => {
    const { step } = this.state;

    switch (step) {
      case STEPS.FILL_DETAILS:
        this.props.onSubmit({ ...values, ...owner });
        break;
      case STEPS.CHOOSE_LICENSEPLATE:
        this.props.dispatchVehicleByLicensePlateGet(values.licensePlate);
        this.setState({ licensePlate: values.licensePlate });
        break;
      case STEPS.ADD_TO_PRIVATE_LIST:
        this.props.dispatchAddVehicleToPrivateListPost(
          {...(this.props.vehicleByLicensePlateFetch.value || {}), hasActiveAccessRights: false },
          () => history.push(this.props.returnRoute)
        );
        break;
      default:
        break;
    }
  };

  validate = values => {
    const { t } = this.props;
    const errors = validateVehicleFormFields(values, t);

    if (!values.licensePlate || !values.licensePlate.trim()) {
      errors.licensePlate = t('noLicensePlateErr');
    }

    return errors;
  };

  state = {
    step: this.props.editMode ? STEPS.FILL_DETAILS : STEPS.CHOOSE_LICENSEPLATE,
  };

  render() {
    const {
      submitProcessing,
      dataLoading,
      data,
      t,
      returnRoute,
      vehicleByLicensePlateFetch,
      addVehicleToPrivateListFetch,
      licensePlateHasSpecialChars,
    } = this.props;
    const { step } = this.state;

    return (
      <FormModal
        header={this.getHeader()}
        dataLoading={dataLoading}
        submitProcessing={
          submitProcessing ||
          vehicleByLicensePlateFetch.pending ||
          addVehicleToPrivateListFetch.pending
        }
        onSubmit={this.handleSubmit}
        data={data}
        validate={this.validate}
        form={FORM_NAME}
        cancelRoute={returnRoute}
        cancelButtonContent={t('cancel')}
        saveButtonIcon={STEP_SAVE_BUTTON_MAP[step].icon}
        saveButtonIconPosition={STEP_SAVE_BUTTON_MAP[step].iconPosition}
        saveButtonContent={t(STEP_SAVE_BUTTON_MAP[step].content)}
      >
        {
          {
            [STEPS.CHOOSE_LICENSEPLATE]: (
              <Fragment>
                <Field
                  label={t('licensePlate')}
                  placeholder={t('licensePlate')}
                  name="licensePlate"
                  component={FieldComponent}
                  valueComponent={InputFieldValueComponent}
                  disabled={submitProcessing || dataLoading || vehicleByLicensePlateFetch.pending}
                  autoFocus
                  fluid
                />
                {licensePlateHasSpecialChars && (
                  <Segment>{t('licensePlateContainsSpecialChars')}</Segment>
                )}
              </Fragment>
            ),
            [STEPS.ADD_TO_PRIVATE_LIST]: (
              <ExistingVehicleMessage vehicle={vehicleByLicensePlateFetch.value} />
            ),
            [STEPS.FILL_DETAILS]: (
              <VehicleFormFields
                submitProcessing={submitProcessing}
                dataLoading={dataLoading}
                data={data}
              />
            ),
          }[step]
        }
      </FormModal>
    );
  }
}

VehicleFormModal.propTypes = {
  t: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dataLoading: PropTypes.bool,
  submitProcessing: PropTypes.bool.isRequired,
  header: PropTypes.string.isRequired,
  returnRoute: PropTypes.string.isRequired,
  data: PropTypes.object,
  editMode: PropTypes.bool,
  dispatchVehicleByLicensePlateGet: PropTypes.func.isRequired,
  vehicleByLicensePlateFetch: PropTypes.object.isRequired,
  addVehicleToPrivateListFetch: PropTypes.object.isRequired,
  dispatchAddVehicleToPrivateListPost: PropTypes.func.isRequired,
  licensePlateHasSpecialChars: PropTypes.bool.isRequired,
};

VehicleFormModal.defaultProps = {
  dataLoading: false,
  data: {},
  editMode: false,
};

const selector = formValueSelector(FORM_NAME);
const hasSpecialChars = /[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

export default compose(
  vehicleByLicensePlateService,
  addVehicleToPrivateListService,
  withTranslation(),
  connect(state => {
    const licensePlate = selector(state, 'licensePlate');
    let licensePlateHasSpecialChars = false;
    if (licensePlate) {
      licensePlateHasSpecialChars = hasSpecialChars.test(licensePlate);
    }
    return {
      licensePlateHasSpecialChars,
    };
  })
)(VehicleFormModal);
